/**
 * ContactsContext - Context para gestión global de contactos
 * 
 * Centraliza el estado y operaciones CRUD de contactos para
 * evitar prop drilling y duplicación de lógica.
 */

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { setCache, invalidateCache } from '../utils/localCache';
import { AuthContext } from './AuthContext';
import { useSocket } from './SocketContext';
import { changeContactStage, updateContactName, deleteContact as deleteContactService, deleteContactsBatch } from '../services/contactsService';
import { apiFetch } from '../utils/apiClient';

// ============================================================
// 1. CONTEXT
// ============================================================
const ContactsContext = createContext(null);
const INCOMPLETE_CONTACTS_MESSAGE = 'No se pudieron cargar todos los contactos; recargá la página';

// ============================================================
// 2. HOOK PERSONALIZADO
// ============================================================
/**
 * Hook para acceder al contexto de contactos
 * @returns {Object} Estado y funciones del contexto
 * @throws {Error} Si se usa fuera de ContactsProvider
 */
export const useContacts = () => {
    const context = useContext(ContactsContext);
    if (!context) {
        throw new Error('useContacts debe usarse dentro de ContactsProvider');
    }
    return context;
};

// ============================================================
// 3. PROVIDER
// ============================================================
/**
 * Provider para el contexto de contactos
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export const ContactsProvider = ({ children }) => {
    const { currentUser } = useContext(AuthContext);
    const userId = currentUser?.uid;

    // --- ESTADO DE CONTACTOS ---
    const [contacts, setContacts] = useState([]);
    const [googleContacts, setGoogleContacts] = useState([]);

    // 🏷️ Sincroniza cambios de ETIQUETA (stage) hechos en OTRAS secciones (ej. Chat en vivo).
    // - Si el contacto YA está en la lista → se actualiza su etapa al instante.
    // - Si NO estaba (recién PROMOVIDO al CRM al asignarle etiqueta) → recarga para que aparezca.
    const { socket } = useSocket();
    const contactsListRef = useRef([]);
    const loadContactsRef = useRef(null);
    contactsListRef.current = contacts; // mantener ref fresca para chequear existencia
    useEffect(() => {
        if (!socket) return;
        const norm = (x) => String(x || '').replace(/@.*$/, '').replace(/\D/g, '');
        const onStageUpdated = (data) => {
            if (!data || !data.stage) return;
            const targetDigits = norm(data.chatId || data.contactId);
            const matches = (c) => {
                const cDigits = norm(c.id || c.phone);
                return (data.contactId && c.id === data.contactId) || (targetDigits && cDigits && cDigits === targetDigits);
            };
            const exists = (contactsListRef.current || []).some(matches);
            if (exists) {
                setContacts((prev) => prev.map((c) => (matches(c) ? { ...c, stage: data.stage } : c)));
            } else if (loadContactsRef.current) {
                // Recién promovido al CRM → recargar para que aparezca en la lista
                try { loadContactsRef.current(new AbortController().signal); } catch (_) { /* noop */ }
            }
        };
        socket.on('contact_stage_updated', onStageUpdated);
        return () => socket.off('contact_stage_updated', onStageUpdated);
    }, [socket]);

    // --- ESTADO DE UI ---
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [feedback, setFeedback] = useState({ type: '', message: '' });

    // Refs para evitar race conditions y memory leaks
    const feedbackTimerRef = useRef(null);
    const isMountedRef = useRef(true);
    const refreshAbortRef = useRef(null);

    // Efecto para trackear si el componente está montado
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
        };
    }, []);

    // --- FUNCIONES DE FEEDBACK ---

    /**
     * Limpia el feedback
     */
    const clearFeedback = useCallback(() => {
        if (isMountedRef.current) {
            setFeedback({ type: '', message: '' });
        }
    }, []);

    /**
     * Muestra un mensaje de feedback y lo limpia automáticamente
     * @param {'success'|'error'|'warning'|'info'} type - Tipo de mensaje
     * @param {string} message - Mensaje a mostrar
     * @param {boolean} autoClear - Si limpiar automáticamente (default: true)
     */
    const showFeedback = useCallback((type, message, autoClear = true) => {
        if (!isMountedRef.current) return;

        // Limpiar timer anterior si existe
        if (feedbackTimerRef.current) {
            clearTimeout(feedbackTimerRef.current);
            feedbackTimerRef.current = null;
        }

        setFeedback({ type, message });

        if (autoClear) {
            feedbackTimerRef.current = setTimeout(() => {
                if (isMountedRef.current) {
                    setFeedback({ type: '', message: '' });
                }
            }, 3000);
        }
    }, []);

    // --- FUNCIONES CRUD ---

    /**
     * Carga contactos desde Firebase
     *
     * 🔧 FIX (techo de 1000): antes pedía `/contacts?limit=1000` de un solo golpe. El backend
     * topa el limit en 1000 (routes/contacts.js) y devuelve `hasMore`/`lastId`, que el front
     * IGNORABA → con más de 1000 contactos la lista mostraba solo los 1000 más recientes y el
     * resto desaparecía SIN error visible. Ahora se recorre el cursor hasta agotar la colección.
     *
     * @param {AbortSignal} [signal] - Signal para cancelar la petición
     */
    const loadContacts = useCallback(async (signal) => {
        if (!userId) return;

        const cacheKey = `contacts_${userId}`;
        const PAGE_SIZE = 500;            // el backend topa en 1000; 500 mantiene la query liviana
        const MAX_PAGES = 60;             // techo de seguridad: 30.000 contactos

        try {
            if (isMountedRef.current) setLoading(true);
            setFeedback(previous => previous.type === 'warning' && previous.message === INCOMPLETE_CONTACTS_MESSAGE
                ? { type: '', message: '' } : previous);
            // console.log('📥 [ContactsContext] Cargando contactos...');

            const token = await currentUser.getIdToken();

            let contactsList = [];
            let startAfter = null;
            const seenIds = new Set();
            const seenCursors = new Set();

            for (let page = 0; page < MAX_PAGES; page++) {
                const url = startAfter
                    ? `/contacts?limit=${PAGE_SIZE}&startAfter=${encodeURIComponent(startAfter)}`
                    : `/contacts?limit=${PAGE_SIZE}`;

                const response = await apiFetch(url, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                // Verificar si fue abortado o desmontado
                if (signal?.aborted || !isMountedRef.current) return;

                if (!response.ok) throw new Error('Error en API');

                const data = await response.json();
                if (signal?.aborted || !isMountedRef.current) return;
                const items = data.items || [];
                const newItems = items.filter(contact => {
                    if (seenIds.has(contact.id)) return false;
                    seenIds.add(contact.id);
                    return true;
                });
                contactsList = contactsList.concat(newItems);

                // Pintar lo que ya llegó: con miles de contactos la primera página se ve de inmediato
                if (isMountedRef.current) setContacts(contactsList);

                // No confiar en hasMore: algunos adaptadores ignoran el cursor.
                if (data.hasMore && (newItems.length === 0 || seenCursors.has(data.lastId))) {
                    showFeedback('warning', INCOMPLETE_CONTACTS_MESSAGE, false);
                    break;
                }
                if (!data.hasMore || !data.lastId) break;
                seenCursors.add(data.lastId);
                startAfter = data.lastId;
            }

            if (isMountedRef.current) {
                setContacts(contactsList);
                // Actualizar caché
                setCache(cacheKey, contactsList, 5 * 60 * 1000); // 5 minutos
            }
        } catch (error) {
            if (signal?.aborted) return;
            // console.error('[ContactsContext] Error cargando contactos:', error);
            showFeedback('error', 'Error al cargar contactos');
        } finally {
            if (isMountedRef.current && !signal?.aborted) {
                setLoading(false);
            }
        }
    }, [userId, showFeedback]);

    // 🏷️ Ref a loadContacts para el listener de etiquetas (que se declara antes que loadContacts)
    loadContactsRef.current = loadContacts;

    /**
     * Recarga contactos forzando una nueva lectura
     */
    const refreshContacts = useCallback(async () => {
        if (!userId) return;

        // Abortar refresh anterior si existe
        if (refreshAbortRef.current) {
            refreshAbortRef.current.abort();
        }

        invalidateCache(`contacts_${userId}`);
        refreshAbortRef.current = new AbortController();

        try {
            await loadContacts(refreshAbortRef.current.signal);
        } finally {
            refreshAbortRef.current = null;
        }
    }, [userId, loadContacts]);

    /**
     * Cambia la etapa de un contacto
     */
    const changeStage = useCallback(async (contactId, newStage) => {
        if (!userId) return false;

        try {
            await changeContactStage(contactId, newStage);

            // Actualizar estado local
            setContacts(prev => prev.map(contact =>
                contact.id === contactId
                    ? { ...contact, stage: newStage }
                    : contact
            ));

            // Invalidar caché
            invalidateCache(`contacts_${userId}`);

            showFeedback('success', 'Segmento actualizado');
            return true;
        } catch (error) {
            // console.error('[ContactsContext] Error al cambiar etapa:', error);
            showFeedback('error', 'Error al actualizar segmento');
            return false;
        }
    }, [userId, showFeedback]);

    /**
     * Actualiza el nombre de un contacto con validación
     */
    const updateName = useCallback(async (contactId, newName) => {
        if (!userId) return false;

        const trimmedName = newName?.trim();
        if (!trimmedName || trimmedName.length < 2 || trimmedName.length > 100) {
            showFeedback('error', 'El nombre debe tener entre 2 y 100 caracteres');
            return false;
        }

        try {
            await updateContactName(contactId, trimmedName);

            // Actualizar estado local
            setContacts(prev => prev.map(contact =>
                contact.id === contactId
                    ? { ...contact, name: trimmedName }
                    : contact
            ));

            // Invalidar caché
            invalidateCache(`contacts_${userId}`);

            showFeedback('success', 'Nombre actualizado correctamente');
            return true;
        } catch (error) {
            // console.error('[ContactsContext] Error al actualizar nombre:', error);
            showFeedback('error', 'Error al actualizar nombre');
            return false;
        }
    }, [userId, showFeedback]);

    /**
     * Actualiza campos de un contacto
     */
    const updateContact = useCallback(async (contactId, updates) => {
        if (!userId) return false;

        // Whitelist de campos permitidos
        const ALLOWED_FIELDS = [
            'name', 'phone', 'email', 'stage',
            'notes', 'facebook', 'instagram', 'waId', 'pushname',
            'lastInteractionAt', 'isGoogleContact',
            'syncedToGoogle', 'googleId', 'source', 'platform'
        ];

        const sanitizedUpdates = Object.keys(updates)
            .filter(key => ALLOWED_FIELDS.includes(key))
            .reduce((obj, key) => {
                obj[key] = updates[key];
                return obj;
            }, {});

        if (Object.keys(sanitizedUpdates).length === 0) {
            showFeedback('error', 'No hay campos válidos para actualizar');
            return false;
        }

        try {
            const token = await currentUser.getIdToken();
            const response = await apiFetch(`/contacts/${encodeURIComponent(contactId)}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(sanitizedUpdates)
            });

            if (!response.ok) throw new Error('Error en API');

            // Leer JSON de respuesta para obtener el posible newId si el teléfono cambió
            const data = await response.json().catch(() => ({}));
            const finalNewId = data.newId || contactId;

            // Actualizar estado local
            setContacts(prev => prev.map(contact =>
                contact.id === contactId
                    ? { ...contact, ...sanitizedUpdates, id: finalNewId }
                    : contact
            ));

            invalidateCache(`contacts_${userId}`);
            return true;
        } catch (error) {
            // console.error('[ContactsContext] Error al actualizar contacto:', error);
            showFeedback('error', 'Error al actualizar contacto');
            return false;
        }
    }, [userId, showFeedback]);

    /**
     * Elimina un contacto
     */
    const removeContact = useCallback(async (contactId) => {
        if (!userId) return false;

        try {
            await deleteContactService(contactId);

            // Actualizar estado local
            setContacts(prev => prev.filter(contact => contact.id !== contactId));
            invalidateCache(`contacts_${userId}`);

            showFeedback('success', 'Contacto eliminado');
            return true;
        } catch (error) {
            // console.error('[ContactsContext] Error al eliminar contacto:', error);
            showFeedback('error', 'Error al eliminar contacto');
            return false;
        }
    }, [userId, showFeedback]);

    /**
     * Elimina múltiples contactos en paralelo (ahora mediante batch API)
     */
    const removeMultipleContacts = useCallback(async (contactIds) => {
        if (!userId || !contactIds?.length) return { success: 0, failed: 0 };

        try {
            // Utilizar el endpoint de batch para evitar Rate Limits (429) y saturación
            const result = await deleteContactsBatch(contactIds);
            
            // Actualizar estado local eliminando los contactos exitosos
            setContacts(prev => prev.filter(contact => !contactIds.includes(contact.id)));
            invalidateCache(`contacts_${userId}`);

            // El backend típicamente devuelve { message, deletedCount }
            const deletedCount = result.deletedCount || contactIds.length;
            showFeedback('success', `${deletedCount} contacto(s) eliminado(s) exitosamente`);
            
            return { success: deletedCount, failed: 0 };
        } catch (error) {
            // console.error('[ContactsContext] Error al eliminar múltiples contactos:', error);
            showFeedback('error', 'Error al eliminar contactos masivamente');
            return { success: 0, failed: contactIds.length };
        }
    }, [userId, showFeedback]);

    // --- CARGA INICIAL ---
    useEffect(() => {
        const abortController = new AbortController();

        if (userId) {
            loadContacts(abortController.signal);
        }

        return () => {
            abortController.abort();
        };
    }, [userId, loadContacts]);

    // --- VALOR DEL CONTEXT ---
    const value = useMemo(() => ({
        contacts,
        googleContacts,
        loading,
        syncing,
        feedback,
        currentUser,
        userId,

        loadContacts,
        refreshContacts,
        updateContact,
        removeContact,
        removeMultipleContacts,
        changeStage,
        updateName,

        setContacts,
        setGoogleContacts,
        setSyncing,
        setLoading,

        showFeedback,
        clearFeedback,
        setFeedback
    }), [
        contacts,
        googleContacts,
        loading,
        syncing,
        feedback,
        userId,

        loadContacts,
        refreshContacts,
        updateContact,
        removeContact,
        removeMultipleContacts,
        changeStage,
        updateName,
        showFeedback,
        clearFeedback
    ]);

    return (
        <ContactsContext.Provider value={value}>
            {children}
        </ContactsContext.Provider>
    );
};

export default ContactsContext;
